import React, { useEffect, useRef, useState } from 'react';
import { Link } from '__models__/models';
import Icon, { Size } from 'components/general/icon/icon';
import LabelWithStarIcon from 'components/talentUser/LabelWithStarIcon';
import Button from 'lj_shared/button/button';
import Tooltip from 'components/general/tooltip/tooltip';
import { useChannel } from 'lib/use_channel';

const styles = require('./links.module.scss');

const LINKEDIN_SYNC_ERROR = 'Unable to fetch your LinkedIn profile. Please try again later.';
const LINKEDIN_TIMEOUT_NOTICE = 'This is taking longer than expected. Please try again.';
// The sync runs in a Sidekiq worker (retry: false); if no broadcast arrives,
// release the button instead of leaving it stuck on "Fetching...". It's a "still
// working, try again" escape, not a failure, and has to cover the queue wait.
const LINKEDIN_SYNC_TIMEOUT_MS = 90000;
// Bridges the success banner across the post-sync page reload.
const SYNC_FLASH_KEY = 'linkedinSyncFlash';

function restoreSyncFlash() {
  const stored = sessionStorage.getItem(SYNC_FLASH_KEY);
  if (!stored) {
    return;
  }

  let flash;
  try {
    flash = JSON.parse(stored);
  } catch {
    sessionStorage.removeItem(SYNC_FLASH_KEY);
    return;
  }

  // The legacy Alerts global can load slightly after React mounts on a fresh
  // page load, so retry briefly instead of dropping the banner on the race.
  let attempts = 0;
  const show = () => {
    if (window.Alerts) {
      sessionStorage.removeItem(SYNC_FLASH_KEY);
      if (flash.level === 'alert') {
        window.Alerts.alert(flash.message);
      } else {
        window.Alerts.notice(flash.message);
      }
    } else if (attempts < 20) {
      attempts += 1;
      setTimeout(show, 150);
    } else {
      sessionStorage.removeItem(SYNC_FLASH_KEY);
    }
  };
  show();
}

interface Props {
  disabled?: boolean;
  headerKey?: string;
  links?: Link[];
  ljColor?: string;
  handshakeProfile?: boolean;
  openModal?: (headerKey: string, arg: any, selector: string) => void;
}

const Links = ({ headerKey, links, ljColor, disabled, handshakeProfile, openModal }: Props) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // True while a sync is in flight; once the watchdog fires we set it false so a
  // late broadcast can't reload the page after we told the user to try again.
  const attemptLiveRef = useRef(false);

  const clearWatchdog = () => {
    if (watchdogRef.current) {
      clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
  };

  const endSync = () => {
    attemptLiveRef.current = false;
    clearWatchdog();
    setIsSyncing(false);
  };

  // Release the timer on unmount so it can't call setState later.
  useEffect(() => clearWatchdog, []);

  // Only the talent's own profile can trigger a sync (the button is gated the
  // same way), so gate the channel subscription on that too — an employer
  // viewing a candidate would open a stream that never receives anything.
  const canSync = !handshakeProfile && Array.isArray(links)
    && links.some(link => link.category_key === 'linkedin');

  // After the post-sync reload, re-show the success banner (see handleReceived).
  useEffect(() => {
    if (canSync) {
      restoreSyncFlash();
    }
  }, [canSync]);

  useChannel<{ status: string; level?: string; message?: string }>(
    canSync ? 'LinkedinSyncChannel' : null,
    (data) => {
      if (!attemptLiveRef.current) {
        return;
      }

      if (data.status === 'complete') {
        // The profile is re-rendered server-side, so reload to show the freshly
        // synced data; carry the banner across via sessionStorage.
        attemptLiveRef.current = false;
        clearWatchdog();
        sessionStorage.setItem(SYNC_FLASH_KEY, JSON.stringify({ level: data.level, message: data.message }));
        window.location.reload();
      } else {
        endSync();
        if (window.Alerts) {
          window.Alerts.alert(data.message || LINKEDIN_SYNC_ERROR);
        }
      }
    }
  );

  if (links === undefined) {
    return null;
  }

  const handleLinkedInSync = () => {
    setIsSyncing(true);
    attemptLiveRef.current = true;
    clearWatchdog();
    watchdogRef.current = setTimeout(() => {
      // Treat a stalled sync as still running rather than a failure.
      endSync();
      if (window.Alerts) {
        window.Alerts.notice(LINKEDIN_TIMEOUT_NOTICE);
      }
    }, LINKEDIN_SYNC_TIMEOUT_MS);

    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '';

    fetch('/users/linkedin_sync', {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken },
    })
      .then(response => {
        // Expect 202 specifically: an expired session redirects to a 200 login
        // page, which response.ok would wrongly accept and leave the UI stuck.
        if (response.status !== 202) {
          throw new Error('LinkedIn sync request failed');
        }
      })
      .catch(() => {
        endSync();
        if (window.Alerts) {
          window.Alerts.alert(LINKEDIN_SYNC_ERROR);
        }
      });
  };

  if (disabled) {
    return (
      <div className={styles.blur}>
        {links.map((link: Link) => {
          return (
            <Icon
              key={link.title.toLowerCase()}
              name={link.category_key !== 'other' ? link.category_key : 'link'}
              color={ljColor || 'flamingo'}
              size={link.category_key === 'twitter' ? Size.QuiteSmall : Size.Medium}
            />
          );
        })}
      </div>
    );
  }

  function renderNoLink(label) {
    return <LabelWithStarIcon label={label} value={false} styles={styles.noLink} />;
  }

  function renderCvButton() {
    return (
      <Button
        onClick={() => openModal(headerKey, null, '#cvFileName')}
        buttonColor='tuftsBlue'
        buttonSize='tiny'
        isRound
      >
        Add a CV
      </Button>
    );
  }

  function checkIfHasLink(category) {
    return links.some(item => item.category_key === category);
  }

  const hasLinkedIn = checkIfHasLink('linkedin');
  const tooltipText = hasLinkedIn
    ? 'Update your profile with the latest information from your LinkedIn account'
    : "You'll need to connect LinkedIn first. Look for the pencil icon to set this up";

  return (
    <>
      {links.map((link: Link) => (
        <a
          key={link.category_key}
          href={link.url}
          target='_blank'
          rel='noopener noreferrer'
          className={styles.linkItem}
        >
          <Icon
            key={link.title.toLowerCase()}
            name={link.category_key !== 'other' ? link.category_key : 'link'}
            color={ljColor || 'flamingo'}
            size={link.category_key === 'twitter' ? Size.QuiteSmall : Size.Medium}

          />
        </a>
      ))}
      {!handshakeProfile && !checkIfHasLink('cv') && renderCvButton()}
      {!handshakeProfile && (
        <Tooltip
          text={tooltipText}
          textBelow={true}
          className={styles.syncButtonWrapper}
        >
          <Button
            onClick={handleLinkedInSync}
            buttonColor={hasLinkedIn ? 'tuftsBlue' : 'silverSand'}
            buttonSize='tiny'
            isRound
            isButton={true}
            disabled={isSyncing || !hasLinkedIn}
            otherClasses={!isSyncing && hasLinkedIn ? styles.linkedinButton : null}
          >
            {isSyncing ? 'Fetching...' : 'Fetch from LinkedIn'}
          </Button>
        </Tooltip>
      )}
    </>
  );
};

export default Links;
